import React, { useState, useRef, useEffect } from 'react';
import { FiChevronDown, FiFilter, FiCheck } from 'react-icons/fi';
import { AnimatePresence, motion } from 'framer-motion';
import { DateRangePicker } from '../common/DateRangePicker';
import type { DateRange } from '../common/DateRangePicker';
import type { GigApplicationStatus } from '../types';

export type ApplicantStatusFilter = 'All' | GigApplicationStatus;

interface ApplicantsFiltersProps {
  statusFilter: ApplicantStatusFilter;
  onStatusFilterChange: (status: ApplicantStatusFilter) => void;
  counts: { all: number; pending: number; accepted: number; rejected?: number };
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  gigFilter: string;
  onGigFilterChange: (gig: string) => void;
  availableGigs?: string[];
}

export const ApplicantsFilters: React.FC<ApplicantsFiltersProps> = ({
  statusFilter,
  onStatusFilterChange,
  counts,
  dateRange,
  onDateRangeChange,
  gigFilter,
  onGigFilterChange,
  availableGigs = [],
}) => {
  const [showGigPicker, setShowGigPicker] = useState(false);
  const gigRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (gigRef.current && !gigRef.current.contains(e.target as Node)) {
        setShowGigPicker(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const filterButtonClasses =
    'inline-flex items-center gap-2 rounded-full border border-white/15 bg-[#13151a] px-4 py-2 text-sm font-medium text-zinc-200 transition hover:border-white/25 hover:bg-white/5 cursor-pointer';

  const filterTabs: { id: ApplicantStatusFilter; label: string; count: number }[] = [
    { id: 'All', label: 'All', count: counts.all },
    { id: 'pending', label: 'Pending', count: counts.pending },
    { id: 'accepted', label: 'Accepted', count: counts.accepted },
  ];

  return (
    <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      {/* Left status pills with count badges */}
      <div className="inline-flex max-w-full items-center overflow-x-auto rounded-full border border-white/10 bg-[#13151a] p-1 no-scrollbar shrink-0">
        {filterTabs.map((tab) => {
          const isActive = statusFilter === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => onStatusFilterChange(tab.id)}
              className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium transition ${
                isActive ? 'bg-zinc-800 text-white shadow-sm' : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              <span>{tab.label}</span>
              <span
                className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs font-bold ${
                  isActive ? 'bg-white text-black' : 'bg-zinc-800 text-zinc-400'
                }`}
              >
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Right dropdown filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Date Filter Dropdown with DateRangePicker */}
        <DateRangePicker value={dateRange} onChange={onDateRangeChange} />

        {/* Gig Filter */}
        <div ref={gigRef} className="relative">
          <button onClick={() => setShowGigPicker((prev) => !prev)} className={filterButtonClasses}>
            <FiFilter size={16} className="text-[#3B82F6]" />
            <span>{gigFilter === 'All' ? 'Filter' : gigFilter}</span>
            <FiChevronDown size={14} className="text-zinc-400" />
          </button>

          <AnimatePresence>
            {showGigPicker && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -8 }}
                transition={{ duration: 0.18 }}
                className="absolute right-0 top-full z-30 mt-3 w-64 rounded-2xl border border-white/15 bg-[#0E1015] p-2 shadow-2xl"
              >
                <button
                  onClick={() => {
                    onGigFilterChange('All');
                    setShowGigPicker(false);
                  }}
                  className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm text-zinc-200 hover:bg-white/5"
                >
                  All Gigs
                  {gigFilter === 'All' && <FiCheck size={16} className="text-blue-500" />}
                </button>
                {availableGigs.map((g) => (
                  <button
                    key={g}
                    onClick={() => {
                      onGigFilterChange(g);
                      setShowGigPicker(false);
                    }}
                    className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm text-zinc-200 hover:bg-white/5"
                  >
                    <span className="truncate">{g}</span>
                    {gigFilter === g && <FiCheck size={16} className="text-blue-500" />}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
